/**
 * Product Card Media Slider
 * Initializes Swiper slider for product card media
 * Optimized with lazy loading and performance in mind
 */

(function() {
  'use strict';

  // Store initialized sliders to avoid re-initialization
  const initializedSliders = new WeakMap();

  /**
   * Initialize slider for a product card
   * @param {HTMLElement} sliderElement - The slider container element
   */
  function initSlider(sliderElement) {
    // Check if already initialized
    if (initializedSliders.has(sliderElement) || sliderElement.classList.contains('swiper-initialized')) {
      return;
    }

    // Count slides
    const slides = sliderElement.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) {
      // Single image, no need for slider - hide navigation
      const navigation = sliderElement.querySelector('.card-media-slider__navigation');
      if (navigation) {
        navigation.style.display = 'none';
      }
      sliderElement.style.opacity = '1';
      return;
    }

    // Get navigation buttons
    const prevButton = sliderElement.querySelector('.card-media-slider__button-prev');
    const nextButton = sliderElement.querySelector('.card-media-slider__button-next');

    // Prevent product link from interfering with navigation buttons
    function setupButtonClickHandler(button) {
      if (!button) return;
      
      button.addEventListener('click', function(e) {
        e.stopPropagation();
        // Swiper will handle the navigation
      });
    }
    
    setupButtonClickHandler(prevButton);
    setupButtonClickHandler(nextButton);

    // Get pagination element
    const paginationElement = sliderElement.querySelector('.card-media-slider__pagination');
    
    // Check if mobile (for loop setting)
    const isMobile = window.innerWidth <= 749;
    
    // Initialize Swiper with optimized settings
    const swiper = new Swiper(sliderElement, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 600,
      loop: !isMobile && slides.length >= 3, // Need at least 3 slides for loop mode
      lazy: {
        loadPrevNext: false, // Only load visible slide for better performance
        loadPrevNextAmount: 0,
        enabled: true,
        checkInView: true,
        loadOnTransitionStart: false, // Only load when slide becomes active
      },
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
      },
      pagination: paginationElement ? {
        el: paginationElement,
        type: 'bullets',
        clickable: false,
        bulletClass: 'card-media-slider__pagination-bullet',
        bulletActiveClass: 'card-media-slider__pagination-bullet-active',
        renderBullet: function(index, className) {
          return '<span class="' + className + '"></span>';
        },
        dynamicBullets: false,
        dynamicMainBullets: 0,
      } : false,
      allowTouchMove: true,
      watchSlidesProgress: true,
      on: {
        init: function() {
          initializedSliders.set(sliderElement, this);
          sliderElement.style.opacity = '1';
          
          // Ensure pagination is visible on mobile
          if (paginationElement && window.innerWidth <= 749) {
            paginationElement.style.display = 'flex';
          }
        },
      },
    });

    // Store reference
    initializedSliders.set(sliderElement, swiper);
  }

  /**
   * Initialize all product card sliders
   */
  function initProductCardSliders() {
    const sliders = document.querySelectorAll('.card-media-slider:not(.swiper-initialized)');
    
    sliders.forEach(slider => {
      initSlider(slider);
    });
  }

  /**
   * Cleanup function for destroyed sliders
   */
  function cleanupSliders() {
    initializedSliders.forEach((swiper, element) => {
      if (!document.contains(element)) {
        if (swiper && typeof swiper.destroy === 'function') {
          swiper.destroy(true, true);
        }
        initializedSliders.delete(element);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCardSliders);
  } else {
    initProductCardSliders();
  }

  // Re-initialize on section load (Shopify theme editor)
  document.addEventListener('shopify:section:load', function() {
    initProductCardSliders();
  });

  // Cleanup on section unload
  document.addEventListener('shopify:section:unload', function() {
    cleanupSliders();
  });

  // Handle window resize
  let resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      initProductCardSliders();
    }, 250);
  }, { passive: true });

  // Lazy load images using Intersection Observer for better performance
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          
          // Load image if it has data-src
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          
          // Load srcset if it has data-srcset
          if (img.dataset.srcset && !img.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          
          // Remove loading attribute once loaded
          if (img.loading === 'lazy') {
            img.loading = 'eager';
          }
          
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '100px', // Start loading 100px before entering viewport
      threshold: 0.01,
    });

    // Observe all lazy images in sliders (only when they're in viewport)
    function observeSliderImages() {
      document.querySelectorAll('.card-media-slider img[loading="lazy"]').forEach(img => {
        // Only observe if not already loaded
        if (img.dataset.src || img.dataset.srcset) {
          imageObserver.observe(img);
        }
      });
    }

    // Initial observation
    observeSliderImages();

    // Re-observe on section load (Shopify theme editor)
    document.addEventListener('shopify:section:load', observeSliderImages);
  }
})();
